import PopupHeader from "@builder/components/app/Popup/PopupHeader";
import "./style.scss";
import OliverText from "@builder/components/general/OliverText";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { closePopup } from "@builder/store/slices/popupSlice";
import { apiFetch } from "jwt-storage-lib";
import { updateNodeProperty } from "@builder/store/slices/BuilderSlice/builderSlice";
import Loading from "@builder/components/app/Loading";
import { useTranslation } from "react-i18next";
import { Upload } from "antd";
import { useUploadAttachmentsMutation } from "@builder/store/slices/attachmentsApiSlice/attachmentsApiSlice";

const { Dragger } = Upload;

const Images = () => {
    const [errorMessage, setErrorMessage] = useState("");
    const dispatch = useAppDispatch();
    const [images, setImages] = useState<{ name: string; id: string; url: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const imagePopupState = useAppSelector(state => state.popup.imageState);
    const companyId = useAppSelector(state => state.builder.build.companyId);
    const { t } = useTranslation();
    const [uploadAttachments] = useUploadAttachmentsMutation();

    useEffect(() => {
        const getImages = async () => {
            setLoading(true);
            if (companyId) {
                const imageReq = await apiFetch(`${import.meta.env.VITE_HUB_API}/attachments/${companyId}`);
                if (!imageReq || imageReq?.ok === false) return;
                const images = await imageReq?.json();

                setImages(images.map((image: any) => ({ id: image._id, url: image.url, name: image.name })));
                setLoading(false);
            }
        };
        getImages();
    }, []);

    const handleFileFromAntd = async (file: File) => {
        setErrorMessage("");
        if (file.size <= 1_572_864 && file.type.split("/")[0] === "image") {
            setLoading(true);
            if (companyId) {
                try {
                    const resp = await uploadAttachments({ companyId: companyId, files: [file] }).unwrap();
                    const image = resp[0];
                    if (image) setImages(prev => [...prev, { name: image.name, id: image._id, url: image.url }]);
                    setLoading(false);
                } catch (e) {
                    console.error("Error saving file", e);
                    setLoading(false);
                }
            }
        } else {
            setErrorMessage("*Invalid file");
        }
        return false;
    };

    const handleSelectImage = (e: any) => {
        const imageId = e.currentTarget.getAttribute("data-id");
        if (imageId) {
            const image = images.find(img => img.id === imageId);
            if (image) {
                if (imagePopupState) {
                    dispatch(updateNodeProperty({ propertyPath: imagePopupState, value: image.url }));
                } else {
                    dispatch(updateNodeProperty({ propertyPath: ["image"], value: image }));
                }
                dispatch(closePopup());
            }
        }
    };

    return (
        <div className="images">
            <PopupHeader src="/image-popup-icon.svg" title="Images" />
            <div className="image-body-wrapper">
                {loading ? (
                    <div className="images-loading-wrapper">
                        <Loading notFullscreen text="Loading images" />
                    </div>
                ) : (
                    <>
                        {images.length === 0 && (
                            <div className="no-images">
                                <OliverText
                                    text="No images to show!"
                                    weight="semi-bold"
                                    className="no-image-font custom-size"
                                    translatePrefix="popup.images."
                                />
                                <OliverText
                                    text="Simply unacceptable! Please add some quick by dragging and dropping below"
                                    weight="light"
                                    className="no-image-font"
                                    translatePrefix="popup.images."
                                />
                                <img src="./no-selector.svg" alt="" />
                            </div>
                        )}
                        <div className="image-popup-file-input">
                            {errorMessage && (
                                <OliverText text={errorMessage} size="s" color="red" className="file-input-error" translatePrefix="popup.images." />
                            )}
                            <Dragger name="file" multiple={false} showUploadList={false} accept="image/*" beforeUpload={handleFileFromAntd}>
                                <div className="custom-file-input">
                                    <img src="./cloud-upload-icon.svg" alt="" />
                                    <OliverText text="Maximum upload size 1.5 MB" size="xs" translatePrefix="popup.images." />
                                    <OliverText text="Drag & drop here" size="s" weight="medium-heavy" translatePrefix="popup.images." />
                                    <OliverText text="or" size="s" weight="medium-heavy" translatePrefix="popup.images." />
                                    <div className="fake-button">
                                        <img src="./paperclip-icon.svg" alt="" />
                                        <span>{t("popup.images.Choose File")}</span>
                                    </div>
                                </div>
                            </Dragger>
                        </div>
                        <div className="image-grid">
                            {images.map((image, index) => (
                                <button key={index} className="image-button" onClick={handleSelectImage} data-id={image.id}>
                                    <div className="image-body">
                                        <img src={image.url} alt="" />
                                    </div>
                                    <div className="image-footer">
                                        <OliverText text={image.name} size="s" weight="medium-heavy" className="image-file-name" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Images;
